import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, SearchX, Landmark, MapPin, Calendar, Users } from "lucide-react";
import { TspCard, TspBreadcrumb, TspEmptyState, TspBadge } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspSearchData from "./useTspSearchData";
import { SEARCH_TYPES, SEARCH_TYPE_LABEL } from "./searchTypes";
import { JOURNEY_STATUS_LABELS, JOURNEY_STATUS_PILL } from "../../constants/tspJourneyStatus";
import { formatDate } from "../../utils/tspFormat";

const SEARCH_DEBOUNCE_MS = 300;

export default function TspSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [input, setInput] = useState(searchParams.get("q") || "");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [activeTypes, setActiveTypes] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setQ((previous) => (previous === input ? previous : input)), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    setSearchParams(q ? { q } : {}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const { data, loading, error, reload } = useTspSearchData(q, activeTypes);

  const toggleType = (key) => setActiveTypes((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]));

  const grouped = useMemo(() => {
    const byType = new Map();
    (data || []).forEach((result) => {
      if (result.type === "parking") return; // Parking is not part of TSP any more
      if (!byType.has(result.type)) byType.set(result.type, []);
      byType.get(result.type).push(result);
    });
    return byType;
  }, [data]);

  const hasQuery = q.trim().length >= 2;

  return (
    <div className="tsp-page tsp-search-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Search</h1>
          <TspBreadcrumb items={["Dashboard", "Search"]} />
        </div>
      </div>

      <TspCard title="Search TSP">
        <label className="tsp-journey-search tsp-search-page-input">
          <SearchIcon size={16} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search journey name, temple, team, taluka/town, state, areas…"
            aria-label="Search TSP"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            autoFocus
          />
        </label>

        <div className="tsp-chip-filter-row" role="group" aria-label="Filter by type">
          {SEARCH_TYPES.map((type) => (
            <button key={type.key} type="button" className="tsp-chip-filter" aria-pressed={activeTypes.includes(type.key)} onClick={() => toggleType(type.key)}>
              {type.label}
            </button>
          ))}
          {activeTypes.length > 0 && (
            <button type="button" className="tsp-filter-clear" onClick={() => setActiveTypes([])}>
              Clear filters
            </button>
          )}
        </div>
      </TspCard>

      {!hasQuery ? (
        <TspEmptyState icon={SearchIcon} title="Start typing to search" message="Search across journeys, temples, teams, talukas / towns, states, and visited locations." />
      ) : error ? (
        <TspLoadError error={error} onRetry={reload} what="search results" />
      ) : loading ? (
        <div className="tsp-page tsp-dashboard-loading">Searching…</div>
      ) : !data || data.length === 0 ? (
        <TspEmptyState icon={SearchX} title="No results" message={`Nothing matched "${q}". Try a different search or clear the type filters.`} />
      ) : (
        [...grouped.entries()].map(([type, results]) => (
          <TspCard key={type} title={SEARCH_TYPE_LABEL[type] || type} action={<TspBadge tone="tag">{results.length}</TspBadge>}>
            <ul className="tsp-search-result-list">
              {results.map((result) => {
                const meta = result.metadata || {};
                const isJourney = result.type === "journey";

                return (
                  <li key={`${result.type}-${result.id}`}>
                    <Link to={result.url} className="tsp-search-result-link">
                      <div className="tsp-search-result-head">
                        <span className="tsp-search-result-title">{result.title}</span>
                        {isJourney && meta.status && (
                          <span className={`tsp-status-pill ${JOURNEY_STATUS_PILL[meta.status] || ""}`}>
                            {JOURNEY_STATUS_LABELS[meta.status] || meta.status}
                          </span>
                        )}
                      </div>

                        {isJourney ? (
                          <div className="tsp-search-result-meta-grid">
                            {meta.temple_name && (
                              <span className="tsp-search-meta-item">
                                <Landmark size={13} aria-hidden="true" />
                                <strong>Temple:</strong> {meta.temple_name}
                              </span>
                            )}
                            {(meta.taluka || meta.city_name) && (
                              <span className="tsp-search-meta-item">
                                <MapPin size={13} aria-hidden="true" />
                                <strong>Taluka / Town:</strong> {meta.taluka || meta.city_name}
                              </span>
                            )}
                            {meta.state_name && (
                              <span className="tsp-search-meta-item">
                                <strong>State:</strong> {meta.state_name}
                              </span>
                            )}
                            {meta.team_name && (
                              <span className="tsp-search-meta-item">
                                <Users size={13} aria-hidden="true" />
                                <strong>Team:</strong> {meta.team_name}
                              </span>
                            )}
                            {(meta.date || meta.start_date) && (
                              <span className="tsp-search-meta-item">
                                <Calendar size={13} aria-hidden="true" />
                                <strong>Date:</strong> {formatDate(meta.date || meta.start_date)}
                              </span>
                            )}
                          </div>
                        ) : (
                          result.subtitle && <span className="tsp-search-result-subtitle">{result.subtitle}</span>
                        )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </TspCard>
        ))
      )}
    </div>
  );
}
